
import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { AdminUser } from '@/types/adminUser';
import { getAdminUsers, addAdminUser, deleteAdminUser, inviteAdminUser } from '@/utils/adminUserService';

export const useAdminUsers = (instanceId: string) => {
  const [isLoading, setIsLoading] = useState(true);
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    loadUsers();
  }, [instanceId]);

  const loadUsers = async () => {
    setIsLoading(true);
    try {
      const fetchedUsers = await getAdminUsers();
      setAdminUsers(fetchedUsers);
    } catch (error) {
      console.error('Error loading admin users:', error);
      toast({
        title: 'Error',
        description: 'Failed to load admin users',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddUser = async (userData: Omit<AdminUser, 'id' | 'status'>) => {
    try {
      await addAdminUser(userData);
      toast({
        title: 'Success',
        description: 'Admin user was added successfully',
      });
      loadUsers();
    } catch (error) {
      console.error('Error adding admin user:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to add admin user',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const handleDeleteUser = async (userId: string) => {
    try {
      await deleteAdminUser(userId);
      toast({
        title: 'Success',
        description: 'Admin user was removed successfully',
      });
      loadUsers();
    } catch (error) {
      console.error('Error deleting admin user:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete admin user',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const handleInviteUser = async (email: string, name: string, role: 'admin' | 'manager') => {
    try {
      await inviteAdminUser(email, name, role);
      toast({
        title: 'Success',
        description: 'Invitation sent successfully',
      });
      loadUsers();
    } catch (error) {
      console.error('Error inviting user:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to send invitation',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const filteredUsers = adminUsers.filter(user => 
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const activeUsers = adminUsers.filter(user => user.status === 'active');
  const pendingInvitations = adminUsers.filter(user => user.status === 'pending');
  const adminOnlyUsers = adminUsers.filter(user => user.role === 'admin');

  return {
    isLoading,
    adminUsers,
    filteredUsers,
    activeUsers,
    pendingInvitations,
    adminOnlyUsers,
    searchTerm,
    setSearchTerm,
    loadUsers,
    handleAddUser,
    handleDeleteUser,
    handleInviteUser
  };
};
